import { Component, ViewChild, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminModalComponent,
  AdminTextareaComponent,
} from '../../admin';
import { AdminToastService } from '../../admin/admin-toast.service';
import { EmailService } from '../../services/email.service';
import { EmailTagsInputComponent } from '../email-tags-input/email-tags-input.component';

@Component({
  selector: 'app-email-modal',
  standalone: true,
  imports: [
    FormsModule,
    AdminModalComponent,
    AdminButtonComponent,
    AdminTextareaComponent,
    EmailTagsInputComponent,
  ],
  template: `
    @if (open()) {
      <app-admin-modal title="Send Email" (closed)="onCancel()">
        <div class="cg-admin-form-grid">
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">To</span>
            <input
              class="cg-input"
              type="email"
              name="to"
              [ngModel]="to"
              disabled
              readonly
              placeholder="lead@example.com"
            />
          </label>

          <div class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Cc</span>
            <app-email-tags-input
              #ccInput
              [(emails)]="ccEmails"
              [disabled]="sending()"
              [excludedEmails]="excludedForCc()"
              placeholder="Add Cc recipients"
            />
          </div>

          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Subject</span>
            <input
              class="cg-input"
              type="text"
              name="subject"
              [(ngModel)]="subject"
              [disabled]="sending()"
              placeholder="CareerGrad Follow-up"
            />
          </label>

          <app-admin-textarea
            class="cg-admin-field--full"
            label="Message"
            name="message"
            [(value)]="message"
            [disabled]="sending()"
            [rows]="6"
            [fullWidth]="true"
          />
        </div>

        @if (formError()) {
          <p class="cg-form-error" role="alert">{{ formError() }}</p>
        }

        <div class="mt-4 flex flex-wrap justify-end gap-2">
          <app-admin-button variant="cancel" type="button" [disabled]="sending()" (click)="onCancel()">
            Cancel
          </app-admin-button>
          <app-admin-button variant="primary" type="button" [disabled]="sending()" (click)="onSend()">
            {{ sending() ? 'Sending…' : 'Send' }}
          </app-admin-button>
        </div>
      </app-admin-modal>
    }
  `,
})
export class EmailModalComponent {
  private readonly emailService = inject(EmailService);
  private readonly toast = inject(AdminToastService);

  readonly open = input(false);
  readonly leadId = input<string | undefined>(undefined);
  readonly applicantId = input<string | undefined>(undefined);
  readonly defaultEmail = input('');

  readonly closed = output<void>();
  readonly sent = output<void>();

  readonly sending = signal(false);
  readonly formError = signal<string | null>(null);
  readonly ccEmails = signal<string[]>([]);

  @ViewChild('ccInput') private ccInput?: EmailTagsInputComponent;

  to = '';
  subject = 'CareerGrad Follow-up';
  message = '';

  constructor() {
    effect(() => {
      if (this.open()) {
        this.to = (this.defaultEmail() ?? '').trim();
        this.subject = 'CareerGrad Follow-up';
        this.message = '';
        this.ccEmails.set([]);
        this.formError.set(null);
      }
    });
  }

  excludedForCc(): string[] {
    return [this.to];
  }

  onCancel(): void {
    if (this.sending()) return;
    this.closed.emit();
  }

  onSend(): void {
    if (this.ccInput && !this.ccInput.commitPending()) return;

    const to = this.to.trim().toLowerCase();
    const subject = this.subject.trim();
    const message = this.message.trim();
    const cc = this.normalizeList(this.ccEmails());

    if (!to || !to.includes('@')) {
      this.formError.set('Primary recipient email is missing.');
      return;
    }
    if (!subject) {
      this.formError.set('Subject is required.');
      return;
    }
    if (!message) {
      this.formError.set('Message is required.');
      return;
    }

    const leadId = this.leadId()?.trim() || undefined;
    const applicantId = this.applicantId()?.trim() || undefined;
    if (!leadId && !applicantId) {
      this.formError.set('Missing recipient context.');
      return;
    }

    this.sending.set(true);
    this.formError.set(null);

    this.emailService
      .sendEmail({
        leadId,
        applicantId,
        to: [to],
        cc: cc.length ? cc : undefined,
        subject,
        message,
      })
      .subscribe({
        next: () => {
          this.sending.set(false);
          this.toast.success('Email sent.');
          this.sent.emit();
          this.closed.emit();
        },
        error: (err: { error?: { message?: string } }) => {
          this.sending.set(false);
          const msg = err.error?.message ?? 'Could not send email.';
          this.formError.set(msg);
          this.toast.error(msg);
        },
      });
  }

  private normalizeList(emails: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const raw of emails) {
      const email = raw.trim().toLowerCase();
      if (!email || seen.has(email)) continue;
      seen.add(email);
      result.push(email);
    }
    return result;
  }
}
